<template>
  <v-dialog v-model="dialog" max-width="1200">
    <v-card>
      <v-toolbar dark color="primary" dense>
        <v-toolbar-title>Borrowing Report</v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <div class="row">
          <div class="col">
            <v-select
              v-model="filters.organizationId"
              :items="organizations"
              item-text="Name"
              item-value="id"
              label="Organization"
              clearable
            ></v-select>
          </div>
          <div class="col">
            <v-select
              v-model="filters.publisher"
              :items="publishers"
              item-text="Name"
              item-value="Name"
              label="Publisher"
              clearable
            ></v-select>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <v-menu v-model="menuFrom" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.dateFrom"
                  label="From"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="filters.dateFrom" @input="menuFrom = false"></v-date-picker>
            </v-menu>
          </div>
          <div class="col">
            <v-menu v-model="menuTo" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.dateTo"
                  label="To"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="filters.dateTo" @input="menuTo = false"></v-date-picker>
            </v-menu>
          </div>
          <div class="col d-flex align-center">
            <v-btn color="primary" class="mr-2" @click="runReport">Filter</v-btn>
            <v-btn color="primary" class="mr-2" @click="downloadReport">Download</v-btn>
            <v-btn text @click="clearFilters">Clear</v-btn>
          </div>
        </div>

        <v-simple-table>
          <thead>
            <tr>
              <th class="text-left">Requested Date</th>
              <th class="text-left">User</th>
              <th class="text-left">Publication</th>
              <th class="text-left">Organization</th>
              <th class="text-left">Publisher</th>
              <th class="text-left">Start Date</th>
              <th class="text-left">End Date</th>
              <th class="text-left">Returned Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list" :key="item.id">
              <td>{{ item.RequestedDate | formatDate }}</td>
              <td>{{ item.user == null ? "" : item.user.firstName }} {{ item.user == null ? "" : item.user.lastName }}</td>
              <td>{{ getTitle(item) }}</td>
              <td>{{ item.organization == null ? "" : item.organization.Name }}</td>
              <td>{{ getPublisher(item) }}</td>
              <td>{{ item.StartDate | formatDate }}</td>
              <td>{{ item.EndDate | formatDate }}</td>
              <td>{{ item.returnedDate | formatDate }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <Pagination :paging="paging" @next="onNextPage" />
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "@/api";
import {Parser} from "json2csv";
import Pagination from "@/components/Pagination";

export default {
  name: "LendingReportDialog",
  components: { Pagination },
  props: {
    isReturned: { type: [Boolean, null], default: null }
  },
  data() {
    return {
      dialog: false,
      list: [],
      organizations: [],
      publishers: [],
      menuFrom: false,
      menuTo: false,
      paging: { count: 10, page: 1, size: 10, keyword: "" },
      filters: { organizationId: 0, publisher: "", dateFrom: "", dateTo: "" }
    };
  },
  methods: {
    async downloadReport() {
      const parser = new Parser();
      const originalPaging = { ...this.paging };
      const allItems = [];
      try {
        // First, get the total count and set page to 1
        this.paging.page = 1;
        const firstResponse = await api.getByPagin("lending/report", this.buildPayload());
        const totalCount = firstResponse.data.count;
        const pageSize = this.paging.size; // original size (10)
        const totalPages = Math.ceil(totalCount / pageSize);

        // Add the first page's data
        allItems.push(...firstResponse.data.data);

        // If there are more pages, fetch them
        for (let page = 2; page <= totalPages; page++) {
          this.paging.page = page;
          const response = await api.getByPagin("lending/report", this.buildPayload());
          allItems.push(...response.data.data);
        }

        const rows = allItems.map(item => ({
          "User Name": item.user == null ? "" : `${item.user.firstName} ${item.user.lastName}`,
          "Organization Name": item.organization == null ? "" : item.organization.Name,
          "Publication Title": item.publicationType === 2 ? item.journal == null ? "" : item.journal.Title : item.publication == null ? "" : item.publication.Title,
          "ISBN": item.publicationType === 2 ? item.journal == null ? "" : item.journal.ISSN : item.publication == null ? "" : item.publication.ISBN,
          "Publisher": item.publicationType === 2 ? item.journal == null ? "" : item.journal.Publisher : item.publication == null ? "" : item.publication.PublisherName || (item.publication.publisher != null && item.publication.publisher.Name ? item.publication.publisher.Name : ""),
          "Author": item.publicationType === 2 ? item.journal == null ? "" : item.journal.Author : item.publication == null ? "" : item.publication.Author,
          "Journal Title": item.publicationType === 2 ? item.journal == null ? "" : item.journal.Title : "",
          "ISSN": item.publicationType === 2 ? item.journal == null ? "" : item.journal.ISSN : "",
          "Journal Provider": item.publicationType === 2 ? item.journal == null ? "" : item.journal.Provider : "",
          "Requested Date": item.RequestedDate,
          "Start Date": item.StartDate,
          "End Date": item.EndDate,
          "Returned Date": item.returnedDate,
          "No Of Licenses": item.licenses,
          "Is Returned": item.isReturned,
        }));
        const csv = parser.parse(rows);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "borrowing-report.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        this.paging = originalPaging;
      }
    },
    open(defaultIsReturned) {
      this.dialog = true;
      if (defaultIsReturned !== undefined) this.isReturned = defaultIsReturned;
      this.loadFilters();
      this.runReport();
    },
    close() {
      this.dialog = false;
    },
    loadFilters() {
      api.getAll("organization").then(r => { this.organizations = r.data || []; });
      api.getAll("publisher").then(r => { this.publishers = r.data || []; });
      // If you also need journal providers, merge in api.getAll("institutions")
    },
    buildPayload() {
      return { ...this.paging, ...this.filters, isReturned: this.isReturned };
    },
    runReport() {
      this.paging.page = 1;
      this.fetch();
    },
    fetch() {
      api.getByPagin("lending/report", this.buildPayload()).then(r => {
        this.list = r.data.data;
        this.paging.count = r.data.count;
      });
    },
    onNextPage(page) {
      this.paging.page = page;
      this.fetch();
    },
    clearFilters() {
      this.filters = { organizationId: 0, publisher: "", dateFrom: "", dateTo: "" };
    },
    getTitle(item) {
      if (item.publicationType === 2) return item.journal == null ? "" : item.journal.Title;
      return item.publication == null ? "" : item.publication.Title;
    },
    getPublisher(item) {
      if (item.publicationType === 2) return item.journal == null ? "" : item.journal.Publisher;
      if (item.publication == null) return "";
      if (item.publication.publisher != null && item.publication.publisher.Name) return item.publication.publisher.Name;
      return item.publication.PublisherName || "";
    }
  }
};
</script>


// WEBPACK FOOTER //
// src/components/LendingReportDialog.vue